import {
  CAPTCHA_PLACEMENTS,
  PAGE_MODES,
  checkDefinition,
  type CaptchaPlacement,
  type PageMode,
  coreT,
  sectionPinReason,
  sectionRuns,
  type Definition,
  type Issue,
  type Question,
  type SectionOptions,
  THEME_ZOOMS,
} from '@survey/core';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CloudOff,
  CornerDownRight,
  GripVertical,
  FolderPlus,
  Layers,
  ListOrdered,
  ListPlus,
  Loader2,
  MessageSquareText,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  Shuffle,
  Redo2,
  SlidersHorizontal,
  Undo2,
  Workflow,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, SURVEY_UPDATED_EVENT } from '../api/client';
import type { SurveyDetail } from '../api/types';
import { AllQuestions } from '../components/builder/AllQuestions';
import { FlowEditor } from '../components/builder/FlowEditor';
import { QuestionEditor } from '../components/builder/QuestionEditor';
import { AddQuestionModal } from '../components/builder/AddQuestionModal';
import { SharePanel } from '../components/builder/SharePanel';
import { AccessibilityCard, EndingsCard, HiddenFieldsCard, LanguagesCard, LoopsCard, QuotasCard, ThemeCard } from '../components/builder/SettingsPanels';
import { addToSection, dependents, duplicateQuestion, importedQuestion, logicFrom, moveQuestion, moveQuestionTo, moveSectionTo, newSection, randomizerOfSection, removeQuestion, removeSection, renameSection, sectionTitles, startSection, TYPE_ICONS, updateQuestion } from '../components/builder/model';
import { SurveyHeader, type SurveyTab } from '../components/SurveyHeader';
import { CollapsibleCard, ForceOpen } from '../components/CollapsibleCard';
import { SurveyRunner } from '../components/SurveyRunner';
import { confirmAction, Empty, Field, Menu, Modal, PageLoader, Switch, useToast } from '../components/ui';
import { ColResizer, useMedia } from '../components/Pane';
import { useAutosave, type SaveStatus } from '../hooks/useAutosave';
import { useHistoryState } from '../hooks/useHistoryState';
import { usePref } from '../hooks/usePref';
import { useCollapsed, useStoredState } from '../hooks/useStoredState';
import { formatNumber, relativeTime } from '../format';
import { useI18n } from '../i18n';

interface Draft {
  title: string;
  description: string;
  requireAuth: boolean;
  definition: Definition;
}

const toDraft = (s: SurveyDetail): Draft => ({ title: s.title, description: s.description, requireAuth: s.requireAuth, definition: s.definition });

export function BuilderPage() {
  const { id = '', tab = 'build' } = useParams();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [loadError, setLoadError] = useState('');
  const { t } = useI18n();

  const load = useCallback(async () => {
    try {
      setSurvey(await api.getSurvey(id));
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : t('builder.load_failed'));
    }
  }, [id, t]);

  useEffect(() => {
    setSurvey(null);
    void load();
  }, [load]);

  // The assistant saved this questionnaire: start over from the server's copy.
  useEffect(() => {
    const onUpdated = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== id) return;
      setSurvey(null);
      void load();
    };
    window.addEventListener(SURVEY_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(SURVEY_UPDATED_EVENT, onUpdated);
  }, [id, load]);

  if (loadError) return <div className="banner error">{loadError}</div>;
  if (!survey) return <PageLoader />;
  if (!['build', 'settings', 'preview', 'share'].includes(tab)) return <Navigate to={`/surveys/${id}`} replace />;
  if (!survey.permissions.includes('survey.edit') && tab !== 'share') {
    return <Navigate to={`/surveys/${id}/${survey.permissions.includes('responses.view') ? 'responses' : 'share'}`} replace />;
  }
  return <BuilderLoaded key={survey.id} initial={survey} tab={tab as SurveyTab} />;
}

function BuilderLoaded({ initial, tab }: { initial: SurveyDetail; tab: SurveyTab }) {
  const toast = useToast();
  const nav = useNavigate();
  const { t, locale } = useI18n();
  const [server, setServer] = useState(initial);
  const history = useHistoryState<Draft>(() => toDraft(initial));
  const draft = history.value;
  const setDraft = history.set;
  // Coming back from the survey flow page selects the question that was picked there.
  const picked = (useLocation().state as { select?: string } | null)?.select;
  const [selected, setSelected] = useState<string | null>(() => (picked && initial.definition.questions.some((q) => q.id === picked) ? picked : (initial.definition.questions[0]?.id ?? null)));
  const [adding, setAdding] = useState(false);
  /** Section new questions go into (see openAdd). */
  const [addSection, setAddSection] = useState('');
  const [sectionDialog, setSectionDialog] = useState<SectionDialog | null>(null);
  // Titles / Codes: the question list with the editor on the right. Preview: every question as respondents see it, edited in place, with the other settings beside it.
  const [mode, setMode] = useStoredState<'edit' | 'preview'>('survey.builder.mode', 'preview');
  // In Preview mode the question list and the settings share the left column as two tabs, so every question gets the rest of the width.
  const [sideTab, setSideTab] = useState<'questions' | 'settings'>('questions');
  const [showFlow, setShowFlow] = useState(false);
  const [busy, setBusy] = useState(false);
  const revision = useRef(initial.revision);
  const canEdit = server.permissions.includes('survey.edit');
  // Workspace layout: side panel width (0 = default) and collapsed icon rail, remembered per browser.
  const [sideW, setSideW] = useStoredState('survey.builder.side_w', 0);
  const [rail, setRail] = useStoredState('survey.builder.rail', false);
  const narrow = useMedia('(max-width: 1080px)');
  const railOn = rail && !narrow;
  const tabbed = mode === 'preview' && !railOn && draft.definition.questions.length > 0;

  const autosave = useAutosave({
    value: draft,
    delay: 700,
    isConflict: (e) => e instanceof ApiError && e.code === 'revision_conflict',
    save: async (d) => {
      const res = await api.saveSurvey(server.id, { ...d, revision: revision.current });
      revision.current = res.revision;
      setServer(res);
    },
  });

  // Local issues give instant feedback; the server re-checks on every save and on publish.
  const issues: Issue[] = useMemo(() => {
    const list = checkDefinition(draft.definition, locale);
    if (!draft.title.trim()) list.unshift({ message: coreT(locale, 'survey.needs_title') });
    return list;
  }, [draft, locale]);

  const setDefinition = (fn: (d: Definition) => Definition) => setDraft((d) => ({ ...d, definition: fn(d.definition) }));
  // Ctrl/Cmd+Z undoes and Ctrl/Cmd+Shift+Z (or Ctrl+Y) redoes, except while typing, where the field has its own undo.
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) history.undo();
      else if ((k === 'z' && e.shiftKey) || k === 'y') history.redo();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, history.undo, history.redo]);
  const current = draft.definition.questions.find((q) => q.id === selected) ?? null;
  const currentIndex = draft.definition.questions.findIndex((q) => q.id === selected);
  const sectionCount = new Set(draft.definition.questions.map((q) => q.sectionTitle?.trim()).filter(Boolean)).size;
  const outlineSummary = [
    t('builder.total', { count: draft.definition.questions.length }),
    sectionCount ? t('builder.sections_total', { count: sectionCount }) : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const sections = sectionTitles(draft.definition);
  const hasUntitled = draft.definition.questions.some((q) => !q.sectionTitle?.trim());

  /** Opens the add-question dialog with the selected question's section (or the last one) chosen. */
  function openAdd() {
    setAddSection(current?.sectionTitle?.trim() || sections[sections.length - 1] || '');
    setAdding(true);
  }

  function addQuestions(qs: Question[]) {
    if (!qs.length) return;
    setDefinition((d) => addToSection(d, qs, addSection, selected ?? undefined));
    setSelected(qs[0]!.id);
    setAdding(false);
  }

  /** A new section goes after the section of the selected question (or at the end), with a blank first question. */
  function createSection(title: string) {
    const r = newSection(draft.definition, title, current?.sectionTitle?.trim() || undefined);
    setDefinition(() => r.def);
    setSelected(r.firstId);
    setSectionDialog(null);
  }

  async function saveToLibrary(q: Question) {
    // Carried-forward options only exist at answer time; save the question with its own options instead.
    const copy = q.carryForward ? importedQuestion(q) : q;
    try {
      await api.saveToLibrary(copy);
      toast(t('library.saved'));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    }
  }

  function deleteQuestion(q: Question) {
    const deps = dependents(draft.definition, q.id);
    const msg = deps.length ? t('builder.confirm_delete_deps', { count: deps.length }) : t('builder.confirm_delete');
    if (!confirmAction(msg)) return;
    const idx = draft.definition.questions.findIndex((x) => x.id === q.id);
    setDefinition((d) => removeQuestion(d, q.id));
    const rest = draft.definition.questions.filter((x) => x.id !== q.id);
    setSelected(rest[Math.min(idx, rest.length - 1)]?.id ?? null);
  }

  const editorEl = (inline: boolean) =>
    current ? (
      <QuestionEditor
        def={draft.definition}
        question={current}
        index={currentIndex}
        issues={issues.filter((x) => x.questionId === current.id)}
        onChange={(q) => setDefinition((d) => updateQuestion(d, current.id, () => q))}
        onMove={(delta) => setDefinition((d) => moveQuestion(d, current.id, delta))}
        onDuplicate={() => {
          const r = duplicateQuestion(draft.definition, current.id);
          setDefinition(() => r.def);
          setSelected(r.newId);
        }}
        onDelete={() => deleteQuestion(current)}
        onShowFlow={() => setShowFlow(true)}
        onSelect={setSelected}
        onSaveToLibrary={server.permissions.includes('survey.edit') ? () => void saveToLibrary(current) : undefined}
               inlineBasics={inline} />
    ) : null;

  async function lifecycle(action: 'publish' | 'close') {
    setBusy(true);
    try {
      const res = action === 'publish' ? await api.publish(server.id) : await api.close(server.id);
      revision.current = res.revision;
      setServer(res);
      toast(action === 'publish' ? t('builder.published_version', { version: res.publishedVersion }) : t('builder.responses_closed'));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function reloadLatest() {
    const latest = await api.getSurvey(server.id);
    revision.current = latest.revision;
    setServer(latest);
    const d = toDraft(latest);
    autosave.reset(d);
    history.reset(d);
  }

  const view: SurveyDetail = { ...server, ...draft, issues };
  const live = server.status === 'published';

  return (
    <>
      <SurveyHeader
        id={server.id}
        number={server.number}
        code={server.code}
        status={server.status}
        permissions={server.permissions}
        active={tab}
        meta={canEdit ? <SaveIndicator status={autosave.status} savedAt={autosave.savedAt} onRetry={() => void autosave.flush()} /> : null}
        title={
          canEdit ? (
            <input
              className="title-input"
              value={draft.title}
              placeholder={t('common.untitled_questionnaire')}
              maxLength={200}
              aria-label={t('builder.title_label')}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            />
          ) : (
            <h1 className="ellipsis" style={{ fontSize: 26, fontWeight: 500 }}>
              {server.title}
            </h1>
          )
        }
        actions={
          <>
            {canEdit && (
              <>
                <button type="button" className="btn ghost icon" onClick={history.undo} disabled={!history.canUndo} aria-label={t('builder.undo')} title={`${t('builder.undo')} (Ctrl+Z)`}>
                  <Undo2 />
                </button>
                <button type="button" className="btn ghost icon" onClick={history.redo} disabled={!history.canRedo} aria-label={t('builder.redo')} title={`${t('builder.redo')} (Ctrl+Shift+Z)`}>
                  <Redo2 />
                </button>
              </>
            )}
          {server.permissions.includes('survey.publish') && server.status !== 'archived' ? (
            <button
              className="btn primary"
              onClick={() => (issues.length ? nav(`/surveys/${server.id}/share`) : void lifecycle('publish'))}
              disabled={busy || autosave.dirty || (live && !server.hasUnpublishedChanges)}
              title={issues.length ? t('builder.issues_before_publish', { count: issues.length }) : undefined}
            >
              <Rocket />
              {live
                ? server.hasUnpublishedChanges
                  ? t('builder.publish_changes')
                  : t('builder.published')
                : server.publishedVersion
                  ? t('builder.reopen')
                  : t('builder.publish')}
              {issues.length > 0 && <span className="pill orange">{issues.length}</span>}
            </button>
          ) : null}
          </>
        }
      />

      {autosave.status === 'conflict' && (
        <div className="banner warn mb-16">
          <AlertTriangle />
          <span className="grow">{t('builder.conflict')}</span>
          <button className="btn sm" onClick={() => void reloadLatest()}>
            <RefreshCw /> {t('builder.load_latest')}
          </button>
        </div>
      )}

      {tab === 'build' && (
        <div className={`builder ${railOn ? 'rail' : ''}`} style={sideW ? ({ '--side-w': `${sideW}px` } as React.CSSProperties) : undefined}>
          <div className={`builder-side${tabbed ? ' tabbed' : ''}`} data-col="side">
            {tabbed && (
              <div className="seg side-tabs" role="tablist" aria-label={t('builder.side_tabs')}>
                {(['questions', 'settings'] as const).map((k) => (
                  <button key={k} type="button" role="tab" aria-selected={sideTab === k} className={sideTab === k ? 'active' : ''} onClick={() => setSideTab(k)}>
                    {k === 'questions' ? t('builder.side_outline') : currentIndex >= 0 ? t('qe.question_n', { n: currentIndex + 1 }) : t('builder.side_selected')}
                  </button>
                ))}
              </div>
            )}
            {railOn ? (
              <div className="builder-rail card">
                <button
                  type="button"
                  className="rail-btn"
                  aria-expanded={false}
                  aria-label={t('builder.expand_panel')}
                  title={t('builder.expand_panel')}
                  onClick={() => setRail(false)}
                >
                  <div className="tile">
                    <ListOrdered />
                  </div>
                  <span className="rail-count">{draft.definition.questions.length}</span>
                </button>
                <button
                  type="button"
                  className="btn sm icon ghost"
                  aria-label={t('builder.expand_panel')}
                  title={t('builder.expand_panel')}
                  onClick={() => setRail(false)}
                >
                  <PanelLeftOpen />
                </button>
              </div>
            ) : tabbed && sideTab === 'settings' ? (
              <div className="side-pane">{editorEl(true) ?? <p className="small muted card card-pad">{t('all.pick_question')}</p>}</div>
            ) : (
              <CollapsibleCard
                storageKey="survey.builder.questions_open"
                icon={<ListOrdered />}
                title={t('builder.questions')}
                sub={outlineSummary}
                actions={
                  <>
                    {canEdit && (
                      <Menu
                        trigger={(toggle) => (
                          <button className="btn sm" onClick={toggle} aria-haspopup="menu">
                            <Plus /> {t('common.add')} <ChevronDown />
                          </button>
                        )}
                      >
                        {(close) => (
                          <>
                            <button
                              disabled={sections.length === 0}
                              title={sections.length === 0 ? t('builder.question_needs_section') : undefined}
                              onClick={() => {
                                close();
                                openAdd();
                              }}
                            >
                              <ListPlus /> {t('builder.add_question')}
                            </button>
                            <button
                              onClick={() => {
                                close();
                                setSectionDialog({ mode: 'new' });
                              }}
                            >
                              <FolderPlus /> {t('builder.add_section')}
                            </button>
                            {hasUntitled && (
                              <button
                                onClick={() => {
                                  close();
                                  setSectionDialog({ mode: 'add' });
                                }}
                              >
                                <Layers /> {t('builder.group_into_section')}
                              </button>
                            )}
                          </>
                        )}
                      </Menu>
                    )}
                    {!narrow && (
                      <button
                        type="button"
                        className="btn ghost sm icon"
                        aria-pressed={false}
                        onClick={() => setRail(true)}
                        title={t('builder.collapse_panel')}
                        aria-label={t('builder.collapse_panel')}
                      >
                        <PanelLeftClose />
                      </button>
                    )}
                  </>
                }
              >
                {draft.definition.questions.length > 0 ? (
                  <QuestionOutline def={draft.definition} issues={issues} selected={selected} canEdit={canEdit} onSelect={setSelected} onShowFlow={() => setShowFlow(true)} onSectionDialog={setSectionDialog} mode={mode} onMode={setMode} onChange={canEdit ? setDefinition : () => {}} />
                ) : (
                  <p className="small muted" style={{ padding: '0 20px 18px' }}>
                    {t('builder.no_questions')}
                  </p>
                )}
              </CollapsibleCard>
            )}
          </div>

          {!railOn && <ColResizer label={t('builder.resize_panel')} width={sideW} onWidth={setSideW} onReset={() => setSideW(0)} />}

          <div className={`builder-main${mode === 'preview' ? ' preview' : ''}`} style={builderZoom(draft.definition.theme?.zoom)}>
            {mode === 'preview' && draft.definition.questions.length > 0 ? (
              <AllQuestions
                def={draft.definition}
                issues={issues}
                selectedId={selected}
                onSelect={(id) => {
                  setSelected(id);
                  setSideTab('settings');
                }}
                canEdit={canEdit}
                onChangeQuestion={(nq) => setDefinition((d) => updateQuestion(d, nq.id, () => nq))}
                onRenameSection={(firstId, title) => setDefinition((d) => renameSection(d, firstId, title))}
                onMode={setMode}
              />
            ) : current ? (
              editorEl(false)
            ) : (
              <div className="card">
                <Empty icon={<Plus />} title={t('builder.add_first')}>
                  <p className="small">{t(sections.length ? 'builder.add_first_hint' : 'builder.add_first_section_hint')}</p>
                  {canEdit && (
                    <button className="btn primary mt-8" onClick={() => (sections.length ? openAdd() : setSectionDialog({ mode: 'new' }))}>
                      <Plus /> {t(sections.length ? 'builder.add_question' : 'builder.add_section')}
                    </button>
                  )}
                </Empty>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'settings' && (
        <>
          {issues.some((i) => !i.questionId) && (
            <div className="banner warn mb-16">
              <AlertTriangle />
              <div>
                {issues
                  .filter((i) => !i.questionId)
                  .map((i, n) => (
                    <div key={n}>{i.message}</div>
                  ))}
              </div>
            </div>
          )}
          <SettingsTab
            draft={draft}
            setDraft={setDraft}
            setDefinition={setDefinition}
            surveyId={server.id}
            code={server.code}
            onOpenQuestion={(qid) => {
              setSelected(qid);
              nav(`/surveys/${server.id}`);
            }}
          />
        </>
      )}

      {tab === 'preview' && (
        <SurveyRunner
          preview
          openUrl={`/surveys/${server.id}/preview`}
          definition={draft.definition}
          survey={{ code: server.code, number: server.number, version: server.publishedVersion, title: draft.title, description: draft.description }}
          unpublished={live && (server.hasUnpublishedChanges || autosave.dirty)}
        />
      )}

      {tab === 'share' && <SharePanel survey={view} dirty={autosave.dirty} busy={busy} onSurveyUpdate={setServer} onPublish={() => void lifecycle('publish')} onClose={() => void lifecycle('close')} />}

      {showFlow && (
        <Modal title={t('builder.flow_title')} onClose={() => setShowFlow(false)} size="full">
          <FlowEditor
            surveyId={server.id}
            def={draft.definition}
            canEdit={canEdit}
            selectedId={selected}
            onChange={setDefinition}
            openUrl={`/surveys/${server.id}/flow`}
            onSelect={(id) => {
              setSelected(id);
              setShowFlow(false);
            }}
          />
        </Modal>
      )}

      {sectionDialog && (
        <SectionModal
          def={draft.definition}
          dialog={sectionDialog}
          defaultStart={selected ?? draft.definition.questions[0]?.id ?? ''}
          onClose={() => setSectionDialog(null)}
          onNew={createSection}
          onSave={(fn) => {
            setDefinition(fn);
            setSectionDialog(null);
          }}
        />
      )}

      {adding && <AddQuestionModal surveyId={server.id} sections={sections} section={addSection} onSection={setAddSection} onAdd={addQuestions} onClose={() => setAdding(false)} />}
    </>
  );
}

type SectionDialog = { mode: 'new' } | { mode: 'add' } | { mode: 'rename'; firstId: string; title: string };

type OutlineFilter = 'all' | 'logic' | 'issues' | 'required';
const OUTLINE_FILTERS: OutlineFilter[] = ['all', 'logic', 'issues', 'required'];

/** The questionnaire's Display size, applied to the question area so the builder shows it at the size respondents get. */
export function builderZoom(zoom: number | undefined): React.CSSProperties | undefined {
  return typeof zoom === 'number' && zoom !== 1 && THEME_ZOOMS.includes(zoom) ? { zoom: String(zoom) } : undefined;
}

export function QuestionOutline({
  def,
  issues,
  selected,
  canEdit,
  onSelect,
  onShowFlow,
  onSectionDialog,
  mode,
  onMode,
  onChange,
}: {
  def: Definition;
  issues: Issue[];
  selected: string | null;
  canEdit: boolean;
  onSelect: (id: string) => void;
  onShowFlow: () => void;
  /** Opens the section dialog owned by the page (rename). */
  onSectionDialog: (d: SectionDialog) => void;
  mode: 'edit' | 'preview';
  onMode: (m: 'edit' | 'preview') => void;
  onChange: (fn: (d: Definition) => Definition) => void;
}) {
  const { t } = useI18n();
  const [label, setLabel] = usePref<'title' | 'code'>('survey.outline.label', 'title');
  const [grouped, setGrouped] = useStoredState('survey.outline.grouped', true);
  const [collapsed, toggle, setCollapsed] = useCollapsed('survey.outline.collapsed');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<OutlineFilter>('all');
  const listRef = useRef<HTMLDivElement>(null);
  const runs = sectionRuns(def);
  const hasSections = runs.some((r) => r.title);
  const numberOf = new Map(def.questions.map((q, i) => [q.id, i + 1]));
  const issueIds = new Set(issues.map((x) => x.questionId).filter(Boolean));
  // How many other questions react to each question's answer, so the source shows its logic too.
  const leadCount = new Map(def.questions.map((q) => [q.id, logicFrom(def, q.id).length]));
  const hasLogic = (q: Question) => !!(q.visibleIf || q.jumpIf || q.carryForward || leadCount.get(q.id));

  const needle = query.trim().toLowerCase();
  const filtering = needle !== '' || filter !== 'all';
  const matches = (q: Question) => {
    if (filter === 'logic' && !hasLogic(q)) return false;
    if (filter === 'issues' && !issueIds.has(q.id)) return false;
    if (filter === 'required' && !q.required) return false;
    if (!needle) return true;
    return [q.title, q.questionCode, q.sectionTitle, String(numberOf.get(q.id))].some((v) => v?.toLowerCase().includes(needle));
  };
  const counts: Record<OutlineFilter, number> = {
    all: def.questions.length,
    logic: def.questions.filter(hasLogic).length,
    issues: def.questions.filter((q) => issueIds.has(q.id)).length,
    required: def.questions.filter((q) => q.required).length,
  };
  const showGroups = grouped && hasSections;
  const sectionKeys = runs.map((r, i) => (r.title ? `${i}:${r.title}` : '')).filter(Boolean);
  const allCollapsed = sectionKeys.length > 0 && sectionKeys.every((k) => collapsed.has(k));
  const isOpen = (key: string) => filtering || !collapsed.has(key);

  // Drag and drop reordering: one question at a time, within and across
  // sections (drop side = upper/lower half of the target row).
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{ id: string; before: boolean } | null>(null);
  const clearDrag = () => {
    setDragId(null);
    setDropHint(null);
    setDragSec(null);
    setSecHint(null);
  };
  // Sections move as a whole: grab the header, drop on any part of another section.
  const [dragSec, setDragSec] = useState<string | null>(null);
  const [secHint, setSecHint] = useState<{ title: string; before: boolean } | null>(null);

  // Questions currently rendered, in order: used for keyboard navigation and the match count.
  const visible = showGroups
    ? runs.flatMap((run, ri) => (!run.title || isOpen(`${ri}:${run.title}`) ? run.questions.filter(matches) : []))
    : def.questions.filter(matches);
  const matchCount = filtering ? def.questions.filter(matches).length : def.questions.length;

  // Selecting a question elsewhere (editor, flow view) opens its section once.
  useEffect(() => {
    const ri = runs.findIndex((r) => r.questions.some((q) => q.id === selected));
    const key = ri >= 0 && runs[ri]!.title ? `${ri}:${runs[ri]!.title}` : '';
    if (key && collapsed.has(key)) toggle(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (!selected) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-qid="${CSS.escape(selected)}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selected, grouped, filter, query]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!visible.length) return;
    const i = visible.findIndex((q) => q.id === selected);
    let next: number | null = null;
    if (e.key === 'ArrowDown') next = Math.min(visible.length - 1, i + 1);
    else if (e.key === 'ArrowUp') next = Math.max(0, i < 0 ? 0 : i - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = visible.length - 1;
    if (next === null) return;
    e.preventDefault();
    const id = visible[next]!.id;
    onSelect(id);
    listRef.current?.querySelector<HTMLElement>(`[data-qid="${CSS.escape(id)}"]`)?.focus();
  };

  const item = (q: Question) => {
    const Icon = TYPE_ICONS[q.type];
    const text = label === 'code' ? q.questionCode || '' : q.title;
    const empty = label === 'code' ? t('builder.outline_no_code') : t('common.untitled_question');
    const active = q.id === selected;
    const dropBefore = dropHint?.id === q.id && dropHint.before;
    const dropAfter = dropHint?.id === q.id && !dropHint.before;
    return (
      <button
        key={q.id}
        data-qid={q.id}
        role="option"
        aria-selected={active}
        tabIndex={active || (!selected && q === visible[0]) ? 0 : -1}
        className={`outline-item ${active ? 'active' : ''} ${dragId === q.id ? 'dragging' : ''} ${dropBefore ? 'drop-above' : ''} ${dropAfter ? 'drop-below' : ''}`}
        onClick={() => onSelect(q.id)}
        title={label === 'code' ? q.title : q.questionCode || q.title}
        draggable={canEdit}
        onDragStart={(e) => {
          setDragId(q.id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', q.id);
        }}
        onDragEnd={clearDrag}
        onDragOver={(e) => {
          if (!canEdit || !dragId || dragId === q.id) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const rect = e.currentTarget.getBoundingClientRect();
          const before = e.clientY < rect.top + rect.height / 2;
          setDropHint((h) => (h?.id === q.id && h.before === before ? h : { id: q.id, before }));
        }}
        onDrop={(e) => {
          e.preventDefault();
          const before = dropHint?.id === q.id ? dropHint.before : false;
          if (canEdit && dragId && dragId !== q.id) onChange((d) => moveQuestionTo(d, dragId, q.id, before ? 'before' : 'after'));
          clearDrag();
        }}
      >
        <span className="n">{numberOf.get(q.id)}</span>
        <span className={`t ${text ? '' : 'empty'} ${label === 'code' && text ? 'mono' : ''}`}>{text || empty}</span>
        {q.required && <span className="req" title={t('builder.filter.required')}>*</span>}
        {(q.visibleIf || q.jumpIf || q.carryForward) && <span className="pill blue" title={t('builder.has_logic')}>if</span>}
        {!!leadCount.get(q.id) && (
          <span className="pill blue lead" title={t('builder.leads_to', { count: leadCount.get(q.id)! })}>
            <CornerDownRight /> {leadCount.get(q.id)}
          </span>
        )}
        {issueIds.has(q.id) && <span className="warn" title={t('builder.needs_attention')} />}
        <Icon />
      </button>
    );
  };

  return (
    <>
      <div className="outline-tools">
        <div className="search grow">
          <Search />
          <input
            className="input sm"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setQuery('');
              if (e.key === 'ArrowDown' && visible[0]) {
                e.preventDefault();
                onSelect(visible[0].id);
                listRef.current?.querySelector<HTMLElement>(`[data-qid="${CSS.escape(visible[0].id)}"]`)?.focus();
              }
            }}
            placeholder={t('builder.outline_search')}
            aria-label={t('builder.outline_search')}
          />
        </div>
        <button type="button" className="btn ghost sm icon" onClick={onShowFlow} title={t('builder.view_flow')} aria-label={t('builder.flow_title')}>
          <Workflow />
        </button>
        <Menu
          align="right"
          trigger={(toggleMenu) => (
            <button type="button" className={`btn ghost sm icon${filter !== 'all' || label === 'code' ? ' on' : ''}`} onClick={toggleMenu} title={t('builder.view_options')} aria-label={t('builder.view_options')}>
              <SlidersHorizontal />
            </button>
          )}
        >
          {(close) => (
            <div className="outline-menu">
              {mode === 'edit' && (
                <>
                  <span className="menu-label">{t('builder.outline_view')}</span>
                  {(['title', 'code'] as const).map((v) => (
                    <button key={v} type="button" aria-pressed={label === v} className={label === v ? 'on' : ''} onClick={() => { setLabel(v); close(); }}>
                      {label === v ? <Check /> : <span className="menu-gap" />} {t(v === 'title' ? 'builder.outline_titles' : 'builder.outline_codes')}
                    </button>
                  ))}
                  <button type="button" onClick={() => { onMode('preview'); close(); }}>
                    <span className="menu-gap" /> {t('builder.outline_preview')}
                  </button>
                </>
              )}
              <span className="menu-label">{t('builder.filter')}</span>
              {OUTLINE_FILTERS.map((f) =>
                f === 'all' || counts[f] > 0 || filter === f ? (
                  <button key={f} type="button" aria-pressed={filter === f} className={filter === f ? 'on' : ''} onClick={() => { setFilter(f); close(); }}>
                    {filter === f ? <Check /> : <span className="menu-gap" />} {t(`builder.filter.${f}`)} <span className="count muted">{formatNumber(counts[f])}</span>
                  </button>
                ) : null,
              )}
              {hasSections && (
                <>
                  <span className="menu-label">{t('builder.sections_label')}</span>
                  <button type="button" aria-pressed={grouped} onClick={() => setGrouped(!grouped)}>
                    {grouped ? <Check /> : <span className="menu-gap" />} {t('builder.group_sections')}
                  </button>
                  {showGroups && (
                    <button type="button" disabled={filtering} onClick={() => { setCollapsed(allCollapsed ? [] : sectionKeys); close(); }}>
                      <span className="menu-gap" /> {t(allCollapsed ? 'builder.expand_all' : 'builder.collapse_all')}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </Menu>
      </div>
      {counts.issues > 0 && filter !== 'issues' && (
        <button type="button" className="chip warn outline-issues" onClick={() => setFilter('issues')}>
          <AlertTriangle /> {t('builder.filter.issues')} <span className="count">{formatNumber(counts.issues)}</span>
        </button>
      )}
      {filtering && (
        <div className="outline-status small muted">
          <span>{t('builder.outline_matches', { count: matchCount, total: def.questions.length })}</span>
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              setQuery('');
              setFilter('all');
            }}
          >
            {t('builder.clear_filters')}
          </button>
        </div>
      )}
      <div className="outline" role="listbox" aria-label={t('builder.questions')} ref={listRef} onKeyDown={onKeyDown}>
        {matchCount === 0 ? (
          <p className="small muted outline-none">{t('builder.outline_no_match')}</p>
        ) : !showGroups ? (
          def.questions.filter(matches).map(item)
        ) : (
          runs.map((run, ri) => {
            const shown = run.questions.filter(matches);
            if (!run.title) return shown.map(item);
            if (filtering && !shown.length) return null;
            const key = `${ri}:${run.title}`;
            const open = isOpen(key);
            const runIssue = run.questions.some((q) => issueIds.has(q.id));
            const firstId = run.questions[0]!.id;
            return (
              <div
                key={key}
                className={`outline-section${dragSec === run.title ? ' dragging' : ''}${secHint?.title === run.title ? (secHint.before ? ' drop-above' : ' drop-below') : ''}`}
                onDragOver={(e) => {
                  if (!canEdit || !dragSec || dragSec === run.title) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  const rect = e.currentTarget.getBoundingClientRect();
                  const before = e.clientY < rect.top + rect.height / 2;
                  setSecHint((h) => (h?.title === run.title && h.before === before ? h : { title: run.title, before }));
                }}
                onDrop={(e) => {
                  if (!canEdit || !dragSec || dragSec === run.title) return;
                  e.preventDefault();
                  const before = secHint?.title === run.title ? secHint.before : false;
                  const moving = dragSec;
                  clearDrag();
                  onChange((d) => moveSectionTo(d, moving, run.title, before ? 'before' : 'after'));
                }}
              >
                <div
                  className={`outline-section-head${canEdit && !filtering ? ' draggable' : ''}`}
                  draggable={canEdit && !filtering}
                  title={canEdit && !filtering ? t('builder.drag_section') : undefined}
                  onDragStart={(e) => {
                    setDragSec(run.title);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', run.title);
                  }}
                  onDragEnd={clearDrag}
                >
                  {canEdit && !filtering && <GripVertical className="grip" aria-hidden />}
                  <button type="button" className="toggle" aria-expanded={open} onClick={() => toggle(key)} disabled={filtering} aria-label={t('builder.toggle_section', { title: run.title })}>
                    <ChevronRight className={`chev ${open ? 'open' : ''}`} />
                    <span className="t" title={run.title}>
                      {run.title}
                    </span>
                    {!open && runIssue && <span className="warn" title={t('builder.needs_attention')} />}
                    {randomizerOfSection(def, run.title) && (
                      <span className="pill blue rz" title={t('builder.in_randomizer', { present: randomizerOfSection(def, run.title)!.present, total: randomizerOfSection(def, run.title)!.sections.length })}>
                        <Shuffle /> {randomizerOfSection(def, run.title)!.present}/{randomizerOfSection(def, run.title)!.sections.length}
                      </span>
                    )}
                    <span className="count">
                      {filtering ? `${formatNumber(shown.length)}/${formatNumber(run.questions.length)}` : t('builder.section_count', { count: run.questions.length })}
                    </span>
                  </button>
                  {canEdit && (
                    <span className="actions">
                      <button type="button" className="btn ghost sm icon" onClick={() => onSectionDialog({ mode: 'rename', firstId, title: run.title })} title={t('builder.rename_section')} aria-label={t('builder.rename_section')}>
                        <PenLine />
                      </button>
                      <button
                        type="button"
                        className="btn ghost sm icon"
                        onClick={() => confirmAction(t('builder.confirm_remove_section', { title: run.title })) && onChange((d) => removeSection(d, firstId))}
                        title={t('builder.remove_section')}
                        aria-label={t('builder.remove_section')}
                      >
                        <X />
                      </button>
                    </span>
                  )}
                </div>
                {open && shown.map(item)}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

function SectionModal({
  def,
  dialog,
  defaultStart,
  onClose,
  onSave,
  onNew,
}: {
  def: Definition;
  dialog: SectionDialog;
  defaultStart: string;
  onClose: () => void;
  onSave: (fn: (d: Definition) => Definition) => void;
  /** A brand-new section with a blank first question. */
  onNew: (title: string) => void;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState(dialog.mode === 'rename' ? dialog.title : '');
  const [start, setStart] = useState(defaultStart);
  const valid = title.trim().length > 0;
  const submit = () => {
    if (!valid) return;
    if (dialog.mode === 'new') return onNew(title.trim());
    onSave((d) => (dialog.mode === 'rename' ? renameSection(d, dialog.firstId, title) : startSection(d, start, title)));
  };
  return (
    <Modal
      title={t(dialog.mode === 'rename' ? 'builder.rename_section' : dialog.mode === 'add' ? 'builder.group_into_section' : 'builder.add_section')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" disabled={!valid} onClick={submit}>
            {t('common.save')}
          </button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label={t('builder.section_name')} htmlFor="section-name">
          <input id="section-name" className="input" autoFocus value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder={t('qe.section_placeholder')} />
        </Field>
        {dialog.mode === 'add' && (
          <Field label={t('builder.section_starts_at')} htmlFor="section-start" hint={t('builder.section_starts_hint')}>
            <select id="section-start" className="select" value={start} onChange={(e) => setStart(e.target.value)}>
              {def.questions.map((q, i) => (
                <option key={q.id} value={q.id}>
                  {i + 1}. {q.title || t('common.untitled_question')}
                  {q.sectionTitle?.trim() ? ` — ${q.sectionTitle.trim()}` : ''}
                </option>
              ))}
            </select>
          </Field>
        )}
      </form>
    </Modal>
  );
}

type ShuffleMode = 'off' | 'questions' | 'sections' | 'both';

function ShuffleSettings({ def, setSettings }: { def: Definition; setSettings: (patch: Partial<Definition['settings']>) => void }) {
  const { t } = useI18n();
  const s = def.settings;
  const mode: ShuffleMode = s.randomizeQuestions ? (s.randomizeSections ? 'both' : 'questions') : s.randomizeSections ? 'sections' : 'off';
  const titled = sectionRuns(def).filter((r, i, all) => r.title && all.findIndex((x) => x.title === r.title) === i);

  const setMode = (m: ShuffleMode) => setSettings({ randomizeQuestions: m === 'questions' || m === 'both', randomizeSections: m === 'sections' || m === 'both' || undefined });
  const setSection = (title: string, patch: SectionOptions) => {
    const next = { ...s.sectionOptions, [title]: { ...s.sectionOptions?.[title], ...patch } };
    for (const k of Object.keys(next[title]!) as (keyof SectionOptions)[]) if (!next[title]![k]) delete next[title]![k];
    if (!Object.keys(next[title]!).length) delete next[title];
    // Drop options for sections that no longer exist.
    for (const k of Object.keys(next)) if (!titled.some((r) => r.title === k)) delete next[k];
    setSettings({ sectionOptions: Object.keys(next).length ? next : undefined });
  };

  return (
    <>
      <Field label={t('builder.shuffle')} htmlFor="shuffle-mode" hint={mode !== 'off' && titled.length ? t('builder.shuffle.hint') : undefined}>
        <select id="shuffle-mode" className="select" value={mode} onChange={(e) => setMode(e.target.value as ShuffleMode)}>
          {(['off', 'questions', 'sections', 'both'] as const).map((m) => (
            <option key={m} value={m}>
              {t(`builder.shuffle.${m}`)}
            </option>
          ))}
        </select>
      </Field>
      {mode !== 'off' && titled.length > 0 && (
        <div className="stack gap-8">
          <strong className="small">{t('builder.section_shuffle')}</strong>
          {titled.map((run) => {
            const opts = s.sectionOptions?.[run.title] ?? {};
            const reason = sectionPinReason(def, run);
            return (
              <div key={run.title} className="section-shuffle">
                <div className="small ellipsis" title={run.title}>
                  <Shuffle className="muted icon-sm" /> {run.title}
                </div>
                {s.randomizeQuestions && (
                  <Switch checked={!opts.keepQuestionOrder} onChange={(v) => setSection(run.title, { keepQuestionOrder: !v })} label={t('builder.shuffle_questions_here')} />
                )}
                {s.randomizeSections && (
                  <>
                    <Switch checked={reason === null} disabled={reason === 'logic'} onChange={(v) => setSection(run.title, { keepPosition: !v })} label={t('builder.can_move')} />
                    {reason === 'logic' && <p className="hint">{t('builder.pinned_logic')}</p>}
                  </>
                )}
              </div>
            );
          })}
          {s.randomizeSections && sectionRuns(def).some((r) => !r.title) && <p className="hint">{t('builder.pinned_untitled')}</p>}
        </div>
      )}
    </>
  );
}

type SetDraft = React.Dispatch<React.SetStateAction<Draft>>;

/**
 * Settings tab: folding cards in two columns (one on narrow screens) and a search
 * that keeps only the cards whose title, summary or field labels match, opened.
 */
function SettingsTab({
  draft,
  setDraft,
  setDefinition,
  surveyId,
  code,
  onOpenQuestion,
}: {
  draft: Draft;
  setDraft: SetDraft;
  setDefinition: (fn: (d: Definition) => Definition) => void;
  surveyId: string;
  code: string;
  onOpenQuestion: (id: string) => void;
}) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const def = draft.definition;
  const words = (...keys: string[]) => keys.map((k) => t(k as never)).join(' ');
  const sections: { id: string; col: 'left' | 'right'; keywords: string; node: React.ReactNode }[] = [
    {
      id: 'general',
      col: 'left',
      keywords: words('builder.general', 'builder.page_mode', 'builder.captcha', 'builder.shuffle', 'builder.show_progress', 'builder.allow_back', 'builder.show_timer', 'builder.require_auth') + ' captcha',
      node: <SettingsCard draft={draft} setDraft={setDraft} />,
    },
    {
      id: 'messages',
      col: 'left',
      keywords: words('builder.messages', 'builder.description', 'builder.welcome_message', 'builder.thankyou_message'),
      node: <MessagesCard draft={draft} setDraft={setDraft} />,
    },
    {
      id: 'endings',
      col: 'left',
      keywords: words('settings.endings', 'settings.redirect', 'settings.ending_message') + ' ' + (def.endings ?? []).map((e) => e.name).join(' '),
      node: <EndingsCard def={def} update={setDefinition} />,
    },
    {
      id: 'languages',
      col: 'left',
      keywords: words('settings.languages', 'settings.base_language', 'settings.add_language', 'settings.edit_translation'),
      node: <LanguagesCard def={def} update={setDefinition} survey={{ title: draft.title, description: draft.description }} />,
    },
    {
      id: 'theme',
      col: 'left',
      keywords: words('settings.theme', 'settings.theme_primary', 'settings.theme_background', 'settings.theme_font', 'settings.theme_logo'),
      node: <ThemeCard def={def} update={setDefinition} />,
    },
    {
      id: 'quotas',
      col: 'right',
      keywords: words('settings.quotas', 'settings.quota_limit', 'settings.quota_message') + ' ' + (def.quotas ?? []).map((q) => q.name).join(' '),
      node: <QuotasCard def={def} update={setDefinition} surveyId={surveyId} />,
    },
    {
      id: 'hidden',
      col: 'right',
      keywords: words('settings.hidden_fields', 'settings.field_name') + ' ' + (def.settings.hiddenFields ?? []).join(' '),
      node: <HiddenFieldsCard def={def} update={setDefinition} code={code} />,
    },
    { id: 'loops', col: 'right', keywords: words('settings.loops'), node: <LoopsCard def={def} update={setDefinition} /> },
    { id: 'a11y', col: 'right', keywords: words('a11y.title') + ' wcag', node: <AccessibilityCard def={def} onOpenQuestion={onOpenQuestion} /> },
  ];
  const q = query.trim().toLowerCase();
  const shown = q ? sections.filter((s) => s.keywords.toLowerCase().includes(q)) : sections;
  const col = (c: 'left' | 'right') =>
    shown
      .filter((s) => s.col === c)
      .map((s) => (
        <div key={s.id} className="settings-slot">
          {s.node}
        </div>
      ));
  return (
    <ForceOpen.Provider value={!!q}>
      <div className="settings-toolbar">
        <div className="search">
          <Search />
          <input
            className="input"
            type="search"
            value={query}
            placeholder={t('builder.settings_search')}
            aria-label={t('builder.settings_search')}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
          />
        </div>
        {q && <span className="small muted">{t('builder.settings_found', { count: shown.length })}</span>}
      </div>
      {q && shown.length === 0 ? (
        <Empty icon={<Search />} title={t('builder.settings_none')} />
      ) : (
        <div className="settings-layout">
          <div className="settings-col">{col('left')}</div>
          <div className="settings-col">{col('right')}</div>
        </div>
      )}
    </ForceOpen.Provider>
  );
}

/** How the questionnaire behaves for respondents: paging, captcha, order and navigation. */
function SettingsCard({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const s = draft.definition.settings;
  const setSettings = (patch: Partial<Definition['settings']>) =>
    setDraft((d) => ({
      ...d,
      definition: {
        ...d.definition,
        settings: { ...d.definition.settings, ...patch },
      },
    }));
  const { t } = useI18n();
  const shuffle = s.randomizeQuestions ? (s.randomizeSections ? 'both' : 'questions') : s.randomizeSections ? 'sections' : 'off';
  const settingsSummary = [
    t(`builder.page_mode.${s.pageMode ?? 'question'}`),
    t(`builder.captcha.${s.captcha ?? 'both'}`),
    shuffle !== 'off' ? t('builder.shuffle_on') : '',
    draft.requireAuth ? t('builder.login_required') : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <CollapsibleCard storageKey="survey.settings.open.general" icon={<SlidersHorizontal />} title={t('builder.general')} sub={settingsSummary}>
      <div className="card-pad stack gap-16" style={{ paddingTop: 0 }}>
        <div className="settings-fields">
          <Field label={t('builder.page_mode')} htmlFor="page-mode" hint={t(`builder.page_mode.${s.pageMode ?? 'question'}.hint`)}>
            <select
              id="page-mode"
              className="select"
              value={s.pageMode ?? 'question'}
              onChange={(e) => {
                const mode = e.target.value as PageMode;
                setSettings({
                  pageMode: mode === 'question' ? undefined : mode,
                });
              }}
            >
              {PAGE_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`builder.page_mode.${mode}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('builder.captcha')} htmlFor="captcha-placement" hint={t('builder.captcha.hint')}>
            <select
              id="captcha-placement"
              className="select"
              value={s.captcha ?? 'both'}
              onChange={(e) => {
                const placement = e.target.value as CaptchaPlacement;
                setSettings({
                  captcha: placement === 'both' ? undefined : placement,
                });
              }}
            >
              {CAPTCHA_PLACEMENTS.map((placement) => (
                <option key={placement} value={placement}>
                  {t(`builder.captcha.${placement}`)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <ShuffleSettings def={draft.definition} setSettings={setSettings} />
        <div className="switch-grid">
          <Switch checked={s.showProgress} onChange={(v) => setSettings({ showProgress: v })} label={t('builder.show_progress')} />
          <div>
            <Switch checked={s.allowBack} onChange={(v) => setSettings({ allowBack: v })} label={t('builder.allow_back')} />
            <p className="hint" style={{ marginTop: 4 }}>{t(s.allowBack ? 'builder.allow_back_on' : 'builder.allow_back_off')}</p>
          </div>
          <Switch checked={!!s.showTimer} onChange={(v) => setSettings({ showTimer: v || undefined })} label={t('builder.show_timer')} />
          <Switch checked={draft.requireAuth} onChange={(v) => setDraft((d) => ({ ...d, requireAuth: v }))} label={t('builder.require_auth')} />
        </div>
      </div>
    </CollapsibleCard>
  );
}

/** The texts respondents read before and after the questions. */
function MessagesCard({ draft, setDraft }: { draft: Draft; setDraft: SetDraft }) {
  const { t } = useI18n();
  const setDef = (patch: Partial<Definition>) => setDraft((d) => ({ ...d, definition: { ...d.definition, ...patch } }));
  const texts: [string, string][] = [
    [t('builder.description'), draft.description],
    [t('builder.welcome_message'), draft.definition.welcomeMessage ?? ''],
    [t('builder.thankyou_message'), draft.definition.thankYouMessage ?? ''],
  ];
  const sub = texts.map(([label, v]) => `${v.trim() ? '✓' : '–'} ${label}`).join(' · ');
  return (
    <CollapsibleCard storageKey="survey.settings.open.messages" defaultOpen={false} icon={<MessageSquareText />} title={t('builder.messages')} sub={sub}>
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        <Field label={t('builder.description')}>
          <textarea className="textarea" style={{ minHeight: 64 }} value={draft.description} maxLength={2000} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
        </Field>
        <div className="settings-fields">
          <Field label={t('builder.welcome_message')}>
            <textarea
              className="textarea"
              style={{ minHeight: 64 }}
              value={draft.definition.welcomeMessage ?? ''}
              maxLength={2000}
              onChange={(e) => setDef({ welcomeMessage: e.target.value || undefined })}
            />
          </Field>
          <Field label={t('builder.thankyou_message')}>
            <textarea
              className="textarea"
              style={{ minHeight: 64 }}
              value={draft.definition.thankYouMessage ?? ''}
              maxLength={2000}
              onChange={(e) => setDef({ thankYouMessage: e.target.value || undefined })}
            />
          </Field>
        </div>
      </div>
    </CollapsibleCard>
  );
}

export function SaveIndicator({ status, savedAt, onRetry }: { status: SaveStatus; savedAt: Date | null; onRetry: () => void }) {
  const { t } = useI18n();
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(timer);
  }, []);
  if (status === 'saving' || status === 'pending')
    return (
      <span className="save-state" aria-live="polite">
        <Loader2 className="spin" /> {t('save.saving')}
      </span>
    );
  if (status === 'error')
    return (
      <span className="save-state error" aria-live="assertive">
        <CloudOff /> {t('save.error')}{' '}
        <button className="btn ghost sm" onClick={onRetry}>
          {t('save.retry_now')}
        </button>
      </span>
    );
  if (status === 'conflict')
    return (
      <span className="save-state error">
        <AlertTriangle /> {t('save.conflict')}
      </span>
    );
  return (
    <span className="save-state" aria-live="polite">
      <CircleCheck /> {savedAt ? t('save.saved_ago', { ago: relativeTime(savedAt.toISOString()) }) : t('save.all_saved')}
    </span>
  );
}
